import {
  createSeasonStatData,
  CURRENT_SEASON,
  getLastWeekDate,
  getNextWeekDate,
} from 'utils';

export const createHomepageSkaterStats = (teams) =>
  teams
    .map((team) => {
      const { id: teamId, name: teamName, abbreviation, rosters } = team;

      const teamRoster = rosters.reduce(
        (total, current) => [...total, current.players[0]],
        [],
      );

      return teamRoster.map((player) => {
        const {
          id,
          firstName,
          lastName,
          jersey,
          jerseySecondary,
          position,
          positionSecondary,
          attendance_aggregate: {
            aggregate: { count: gamesPlayed },
          },
          goals_aggregate: {
            aggregate: { count: goals },
          },
          primaryAssist_aggregate: {
            aggregate: { count: primaryAssists },
          },
          secondaryAssist_aggregate: {
            aggregate: { count: secondaryAssists },
          },
          penalties_aggregate: {
            aggregate: {
              sum: { minutes: pim },
            },
          },
        } = player;

        const positionString = `${position.slice(0, 1)}${
          positionSecondary?.length ? `/${positionSecondary.slice(0, 1)}` : ''
        }`;
        const assists = primaryAssists + secondaryAssists;

        const goalieGamesPlayed = player.attendance_aggregate.nodes.reduce(
          (total, currentGame) => {
            if (
              currentGame?.schedule.awayGoalie === id ||
              currentGame?.schedule.homeGoalie === id
            ) {
              return 1 + total;
            }
            return total;
          },
          0,
        );

        const skaterGamesPlayed = gamesPlayed - goalieGamesPlayed;

        return createSeasonStatData(
          {
            id,
            seasonId: CURRENT_SEASON,
            teamId,
          },
          {
            lastName,
            firstName,
            position: positionString,
            team: abbreviation || teamName.slice(0, 3).toUpperCase(),
            jersey: `${jersey ? jersey : ''}${
              jerseySecondary ? ` / ${jerseySecondary}` : ''
            }`,
            gamesPlayed: skaterGamesPlayed || 0,
            goals: goals || 0,
            assists: assists || 0,
            points: goals + assists || 0,
            pim: pim || 0,
            gpg: skaterGamesPlayed
              ? Number(goals / skaterGamesPlayed).toFixed(2)
              : 0,
            apg: skaterGamesPlayed
              ? Number(assists / skaterGamesPlayed).toFixed(2)
              : 0,
            ppg: skaterGamesPlayed
              ? Number((goals + assists) / skaterGamesPlayed).toFixed(2)
              : 0,
            pimpg: skaterGamesPlayed
              ? Number(pim / skaterGamesPlayed).toFixed(2)
              : 0,
          },
        );
      });
    })
    .flat()
    .filter(
      (entry) =>
        !entry.position.includes('G') ||
        (entry.position.includes('G') && entry.points > 0),
    );

export const createHomepageGoalieStats = (goalieStats) =>
  Object.entries(goalieStats)
    .map(([teamId, teamGoalies]) => {
      return Object.values(teamGoalies).map((goalie) => {
        const {
          firstName,
          lastName,
          jersey,
          jerseySecondary,
          gamesPlayed,
          goalsAgainst,
          id,
          losses,
          otl,
          shotsAgainst,
          shutouts,
          goals,
          assists,
          team,
          pim,
          wins,
        } = goalie;
        const saves = shotsAgainst - goalsAgainst;
        const gaa = (goalsAgainst / gamesPlayed).toFixed(2);
        let svp = saves / shotsAgainst;
        svp = svp === 1 ? svp.toFixed(3) : svp.toFixed(3).slice(1);
        return createSeasonStatData(
          { id, seasonId: CURRENT_SEASON, teamId },
          {
            lastName,
            firstName,
            team,
            jersey: `${jersey ? jersey : ''}${
              jerseySecondary ? ` / ${jerseySecondary}` : ''
            }`,
            gamesPlayed,
            wins,
            losses,
            otl,
            gaa,
            svp,
            shutouts,
            goalsAgainst,
            shotsAgainst,
            saves,
            goals: goals || 0,
            assists,
            pim: pim || 0,
          },
        );
      });
    })
    .flat();

export const createUpcomingGames = (schedule) => {
  const gamesByWeek = {};
  const lastWeek = getLastWeekDate().toISOString();

  const upcomingGames = [...schedule].filter(
    (game) => new Date(game?.date).toISOString() > lastWeek,
  );
  const playoffGames = [...schedule].filter((game) => game?.playoffs);
  const isPlayoffs = playoffGames?.length
    ? getNextWeekDate().toISOString() >
      new Date(playoffGames[0].date).toISOString()
    : false;

  if (isPlayoffs) {
    playoffGames.forEach((game) => {
      if (gamesByWeek[game?.date]) {
        gamesByWeek[game?.date].push(game);
      } else {
        gamesByWeek[game?.date] = [game];
      }
    });
  } else {
    const gamesToShow = upcomingGames.slice(0, 6).some((game) => game?.cancel)
      ? 7
      : 6;
    upcomingGames.slice(0, gamesToShow).forEach((game) => {
      if (gamesByWeek[game?.date]) {
        gamesByWeek[game?.date].push(game);
      } else {
        gamesByWeek[game?.date] = [game];
      }
    });
  }

  return { gamesByWeek, isPlayoffs };
};
